/**
 * Project Bobcat — a Knowledge Agent created with no knowledge base.
 *
 * The backend always builds the Knowledge Agent template with a RAG lookup:
 * a Gemini step that writes a search query (`save_llm_response_as` →
 * `RAG_SEARCH_QUERY`), then `GetRAGSearchResponse`, which reads that query
 * and searches the corpus named by `live_corpus_id`. The corpus is only
 * provisioned when documents or pasted text are supplied, so when onboarding
 * skips that step `live_corpus_id` stays `''` forever — and Publish waits on
 * it forever.
 *
 * This plans the rewrite that drops the lookup instead: both RAG steps are
 * removed and everything that routed into them routes to the step the
 * search would have succeeded into (the answer step, which the stock
 * template already reaches without RAG context for out-of-scope turns).
 * Pure — the store applies the plan.
 *
 * Rows hold node numbers as canonical strings (PSTUDIO-4453); routing
 * (`whatsNext` targets, `nextNodes`) is matched as numbers, so a row's number
 * is read once through `rowNodeNumber`.
 */
import { isCanonicalNodeNumber } from './node-number';

export const RAG_SEARCH_COMMAND = 'GetRAGSearchResponse';

type ParamObject = Record<string, unknown>;

/** The row fields the plan reads — `TableRow` types these loosely. */
export interface SkipCorpusRow {
  id: string;
  /** Optional on TableNode (group rows have none); a canonical string. */
  nodeNumber?: string | null;
  command?: unknown;
  whatsNext?: unknown;
  nextNodes?: unknown;
  parameterInput?: unknown;
}

export interface SkipCorpusRowUpdate {
  rowId: string;
  updates: {
    whatsNext?: string[];
    nextNodes?: number[];
    parameterInput?: unknown;
  };
}

export interface SkipCorpusPlan {
  /** The RAG search node(s) and the query-writing node(s) feeding them. */
  removeRowIds: string[];
  /** Rows whose routing pointed into the removed steps, rewired past them. */
  rowUpdates: SkipCorpusRowUpdate[];
  /** Per RAG lookup: the removed rows and the step routing now goes to. */
  redirects: { removedRowIds: string[]; targetNodeNumber: number }[];
}

/**
 * A row's node number as the number routing refers to it by, or undefined
 * for an empty cell or text that is not a node number.
 */
export const rowNodeNumber = (row: SkipCorpusRow): number | undefined => {
  if (!isCanonicalNodeNumber(row.nodeNumber)) return undefined;
  const n = Number(row.nodeNumber);
  return Number.isSafeInteger(n) ? n : undefined;
};

export const parseParam = (raw: unknown): ParamObject | null => {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw as ParamObject;
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object'
      ? (parsed as ParamObject)
      : null;
  } catch {
    return null;
  }
};

/** A RAG search node still waiting on a corpus that will never arrive. */
export const isUnprovisionedRagNode = (row: SkipCorpusRow): boolean => {
  if (row.command !== RAG_SEARCH_COMMAND) return false;
  const param = parseParam(row.parameterInput);
  return !!param && param.live_corpus_id === '';
};

export const whatsNextList = (row: SkipCorpusRow): string[] =>
  Array.isArray(row.whatsNext) ? (row.whatsNext as string[]) : [];

const nextNodesList = (row: SkipCorpusRow): number[] | null =>
  Array.isArray(row.nextNodes) ? (row.nextNodes as number[]) : null;

const routesTo = (row: SkipCorpusRow, target: number): boolean =>
  whatsNextList(row).some((w) => w.split('~')[1] === String(target)) ||
  !!nextNodesList(row)?.includes(target);

const successTarget = (row: SkipCorpusRow): number | null => {
  const entry = whatsNextList(row).find((w) => w.split('~')[0] === 'true');
  const n = entry ? Number(entry.split('~')[1]) : NaN;
  return Number.isFinite(n) ? n : null;
};

/**
 * Rewrites node references in the parts of a parameter input that hold
 * destinations — a `destination_nodes` map (SupervisorRouting) and any
 * `*_node`/`*_nodes` key. Deliberately not a blind walk: prompts and model
 * settings hold numbers that are not node numbers.
 */
const rewireParam = (
  param: ParamObject,
  swap: (n: number) => number | null
): ParamObject | null => {
  let changed = false;
  const remap = (value: unknown): unknown => {
    if (typeof value === 'number') {
      const next = swap(value);
      if (next !== null) {
        changed = true;
        return next;
      }
      return value;
    }
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      const next = swap(Number(value));
      if (next !== null) {
        changed = true;
        return String(next);
      }
      return value;
    }
    if (Array.isArray(value)) return value.map(remap);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as ParamObject).map(([k, v]) => [k, remap(v)])
      );
    }
    return value;
  };
  const next: ParamObject = { ...param };
  Object.keys(next).forEach((key) => {
    if (key === 'destination_nodes' || /_nodes?$/.test(key)) {
      next[key] = remap(next[key]);
    }
  });
  return changed ? next : null;
};

export const planSkipKnowledgeCorpus = (
  rows: SkipCorpusRow[]
): SkipCorpusPlan | null => {
  const ragRows = rows.filter(isUnprovisionedRagNode);
  if (!ragRows.length) return null;

  // Removed node number → where routing into it should go instead.
  const redirect = new Map<number, number>();
  const removeRowIds: string[] = [];
  const redirects: SkipCorpusPlan['redirects'] = [];

  for (const rag of ragRows) {
    const ragNumber = rowNodeNumber(rag);
    if (ragNumber === undefined) continue;
    const target = successTarget(rag);
    // Without a success step there is nothing safe to route to; leave it.
    if (target === null) continue;
    const inputVar = parseParam(rag.parameterInput)?.input_var;
    const queryWriters = rows.filter(
      (row) =>
        row.id !== rag.id &&
        typeof inputVar === 'string' &&
        parseParam(row.parameterInput)?.save_llm_response_as === inputVar &&
        routesTo(row, ragNumber)
    );
    const lookup = [...queryWriters, rag];
    lookup.forEach((row) => {
      const n = rowNodeNumber(row);
      if (n !== undefined) redirect.set(n, target);
      removeRowIds.push(row.id);
    });
    redirects.push({
      removedRowIds: lookup.map((row) => row.id),
      targetNodeNumber: target,
    });
  }
  if (!removeRowIds.length) return null;

  const swap = (n: number) => redirect.get(n) ?? null;
  const removed = new Set(removeRowIds);
  const rowUpdates: SkipCorpusRowUpdate[] = [];

  for (const row of rows) {
    if (removed.has(row.id)) continue;
    const updates: SkipCorpusRowUpdate['updates'] = {};

    const wn = whatsNextList(row);
    if (wn.some((w) => swap(Number(w.split('~')[1])) !== null)) {
      updates.whatsNext = wn.map((w) => {
        const [key, dest] = w.split('~');
        const next = swap(Number(dest));
        return next === null ? w : `${key}~${next}`;
      });
    }

    const nn = nextNodesList(row);
    if (nn && nn.some((n) => swap(n) !== null)) {
      updates.nextNodes = nn.map((n) => swap(n) ?? n);
    }

    const param = parseParam(row.parameterInput);
    const rewired = param ? rewireParam(param, swap) : null;
    if (rewired) {
      updates.parameterInput =
        typeof row.parameterInput === 'string'
          ? JSON.stringify(rewired)
          : rewired;
    }

    if (Object.keys(updates).length)
      rowUpdates.push({ rowId: row.id, updates });
  }

  return { removeRowIds, rowUpdates, redirects };
};
